import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { connect } from "react-redux";
import moment from "moment";
import DashboardDatePicker from "../DashboardDatePicker";
import ChartCard, { ChartTypeToggle, FilterSelect, ResetButton } from "../ChartCard";
import useChartRequest from "../useChartRequest";
import { PALETTE, cartesianOptions, formatMoney, isAllZero, seriesDataset } from "../chartTheme";
import { sendPostRequest } from "../../../../../actions/app-actions";
import { getAdminBusinessId } from "../../../../../utils/authHelper";

const MAX_RANKED = 10;

const StaffCommisionChart = (props) => {
  const { rows, params, loading, refreshing, error, load } = useChartRequest(
    props.sendPostRequest,
    `/admin/get-staff-commission-earned/${getAdminBusinessId()}`
  );
  const [chartType, setChartType] = useState("bar");
  const [period, setPeriod] = useState(new Date());
  const [staffId, setStaffId] = useState("");

  const request = (next = {}) => {
    const p = next.period !== undefined ? next.period : period;
    const s = next.staffId !== undefined ? next.staffId : staffId;
    load({ staff_id: s, month: moment(p).format("MM"), year: moment(p).format("YYYY") });
  };

  useEffect(() => {
    request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRanking = !params.staff_id;

  const ranked = useMemo(
    () =>
      isRanking
        ? [...rows]
            .map((r) => ({ label: r.label, total: Number(r.total) || 0 }))
            .sort((a, b) => b.total - a.total)
            .slice(0, MAX_RANKED)
        : rows.map((r) => ({ label: r.label, total: Number(r.total) || 0 })),
    [rows, isRanking]
  );

  const values = ranked.map((r) => r.total);
  const total = rows.reduce((sum, r) => sum + (Number(r.total) || 0), 0);
  const top = isRanking && ranked.length && ranked[0].total > 0 ? ranked[0] : null;
  const shownMonth = params.month ? moment(`${params.year}-${params.month}-01`, "YYYY-MM-DD") : moment(period);

  const chartData = useMemo(
    () => ({
      labels: ranked.map((r) => r.label),
      datasets: [
        seriesDataset({
          label: "Commission",
          data: ranked.map((r) => r.total),
          color: PALETTE.violet,
          type: isRanking ? "bar" : chartType,
          fill: true,
        }),
      ],
    }),
    [ranked, chartType, isRanking]
  );

  const options = useMemo(() => {
    const base = cartesianOptions({ money: true });
    if (!isRanking) return base;
    return {
      ...base,
      indexAxis: "y",
      interaction: { mode: "nearest", axis: "y", intersect: false },
      scales: {
        x: { ...base.scales.y, grid: { color: "#eef1f5", drawTicks: false } },
        y: { ...base.scales.x, ticks: { ...base.scales.x.ticks, autoSkip: false } },
      },
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: { label: (ctx) => ` Commission: ${formatMoney(ctx.parsed.x)}` },
        },
      },
    };
  }, [isRanking]);

  const actions = (
    <>
      <FilterSelect
        value={staffId}
        options={props.staffs}
        placeholder="All staff"
        onChange={(v) => {
          setStaffId(v);
          request({ staffId: v });
        }}
      />
      <DashboardDatePicker
        mode="month"
        value={period}
        onChange={(val) => {
          const next = val || new Date();
          setPeriod(next);
          request({ period: next });
        }}
      />
      {!isRanking && <ChartTypeToggle value={chartType} onChange={setChartType} />}
      <ResetButton
        onClick={() => {
          const now = new Date();
          setStaffId("");
          setPeriod(now);
          request({ staffId: "", period: now });
        }}
      />
    </>
  );

  const summary =
    !loading && !error ? (
      <div className="dash-figure">
        <span className="dash-figure__value">{formatMoney(total)}</span>
        <span className="dash-figure__meta">
          Commission in {shownMonth.format("MMMM YYYY")}
          {top ? ` · Top earner: ${top.label} (${formatMoney(top.total)})` : ""}
        </span>
      </div>
    ) : null;

  const rankingHeight = Math.max(220, ranked.length * 34 + 40);

  return (
    <ChartCard
      title="Staff commissions"
      subtitle={isRanking ? "Commission earned per staff member" : "Daily commission for the selected staff member"}
      tooltipCode="P3F8"
      actions={actions}
      summary={summary}
      loading={loading}
      refreshing={refreshing}
      error={error}
      empty={isAllZero(values)}
      emptyTitle="No commission earned in this period"
      height={isRanking ? rankingHeight : 280}
    >
      {isRanking || chartType === "bar" ? (
        <Bar data={chartData} options={options} />
      ) : (
        <Line data={chartData} options={options} />
      )}
    </ChartCard>
  );
};

export default connect(null, { sendPostRequest })(StaffCommisionChart);
